'use client'

import { useCancelSubscriptionMutation } from '@/api/user/organization/billing/cancel-subscription-mutation'
import { useCreatePortalSessionMutation } from '@/api/user/organization/billing/create-portal-session-mutation'
import { SettingsSection } from '@/features/settings-modal/settings-section'
import { BillingCancelDialog } from '@/features/subscription/billing/billing-cancel-dialog/billing-cancel-dialog'
import { useBillingCancelDialogProps } from '@/features/subscription/billing/use-billing-cancel-dialog-props'
import { useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { parseApiDate } from '@/utils/date/parse-api-date'
import Image from 'next/image'
import { useBillingUser } from '@/features/subscription/billing/billing-user-context'
import { PLAN_APP_ICON_SRC } from '@/features/subscription/billing/billing-constants'
import { useBillingCapabilities } from '@/features/subscription/billing/billing-capabilities'
import { useSettingsModal } from '@/features/settings-modal/settings-modal-context'
import { AlertCircle } from 'lucide-react'
import { buildShopifyEmbeddedInstallUrl } from '@/features/subscription/billing/shopify-embedded-url'

/** Plan tiers ordered low → high; a plan with a lower-indexed neighbour can be downgraded. */
const PLAN_SORT_ORDER = ['STARTER', 'ADVANCED', 'PRO', 'SCALE', 'CUSTOM']

export const BillingPreferences = () => {
    const billingUser = useBillingUser()
    const organizationId = billingUser?.organizationId ?? ''
    const [isPortalLoading, setIsPortalLoading] = useState(false)
    const [isCancelLoading, setIsCancelLoading] = useState(false)
    const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false)
    const billingAndUsage = billingUser?.billingAndUsage
    const billingPlans = billingUser?.billingPlans
    const {
        canChangePlanInApp,
        canCancelInApp,
        manageBillingUrl,
        externalBillingDashboardLabel,
    } = useBillingCapabilities()

    const { mutateAsync: createPortalSessionAsync } = useCreatePortalSessionMutation()
    const { mutateAsync: cancelSubscriptionAsync } = useCancelSubscriptionMutation()
    const invalidateBillingCache = billingUser?.invalidateBillingCache

    // Save-funnel props for the cancel dialog (downgrade step etc.) — shared with the Plans tab.
    const cancelDialogProps = useBillingCancelDialogProps()

    const currentPlan = useMemo(
        () =>
            billingAndUsage?.billing?.activeSubscription &&
            billingPlans?.plans.find(
                (plan) => plan.type === billingAndUsage?.billing.activeSubscription?.subscriptionType
            ),
        [billingPlans, billingAndUsage]
    )

    const subscription = billingAndUsage?.billing?.activeSubscription
    const isInTrial = Boolean(subscription?.isInTrial)

    const hasSubscriptionRecords = billingAndUsage?.billing?.hasSubscriptionRecords ?? false
    const showInvoiceHistoryOnly = !subscription && hasSubscriptionRecords && canChangePlanInApp
    const showPortalSection =
        !canChangePlanInApp || subscription?.collectionMethod === 'AUTO' || showInvoiceHistoryOnly

    // When a canceling subscription has a scheduled lower-tier plan replacing it, it's a downgrade
    // (not a full cancellation) — name the incoming plan so the copy reflects that.
    const pendingDowngradePlanName = useMemo(() => {
        const pending = (billingAndUsage?.billing?.pendingSubscriptions ?? []).find(
            (p) => p.subscriptionUuid && !p.subscriptionCanceledAt
        )
        if (!pending) return undefined
        return billingPlans?.plans.find((plan) => plan.type === pending.subscriptionType)?.name
    }, [billingAndUsage?.billing?.pendingSubscriptions, billingPlans])

    const shopifyReconnectRequired = billingAndUsage?.billing.shopifyReconnectRequired ?? false
    const shopifyReconnectUrl = billingAndUsage?.billing.shopifyShopDomain
        ? buildShopifyEmbeddedInstallUrl(billingAndUsage.billing.shopifyShopDomain)
        : null
    const cancellationTargets = useMemo(() => {
        const activeTarget =
            subscription?.hasSubscription && subscription.subscriptionUuid && !subscription.subscriptionCanceledAt
                ? [
                      {
                          subscriptionUuid: subscription.subscriptionUuid,
                          subscriptionType: subscription.subscriptionType,
                      },
                  ]
                : []

        const pendingTargets = (billingAndUsage?.billing?.pendingSubscriptions ?? [])
            .filter(
                (pendingSubscription) =>
                    pendingSubscription.subscriptionUuid && !pendingSubscription.subscriptionCanceledAt
            )
            .map((pendingSubscription) => ({
                subscriptionUuid: pendingSubscription.subscriptionUuid,
                subscriptionType: pendingSubscription.subscriptionType,
            }))

        return [...activeTarget, ...pendingTargets]
    }, [billingAndUsage?.billing?.pendingSubscriptions, subscription])

    const canCancelSubscription = cancellationTargets.length > 0
    const canReactivateSubscription = Boolean(
        subscription?.subscriptionCanceledAt && subscription?.subscriptionUuid
    )

    const { setOptions } = useSettingsModal()

    // The next-lower plan available below the current one (skips tiers that aren't offered).
    const downgradeTargetPlan = useMemo(() => {
        if (!currentPlan || !billingPlans) return undefined
        const currentIndex = PLAN_SORT_ORDER.indexOf(currentPlan.type)
        if (currentIndex <= 0) return undefined
        for (let i = currentIndex - 1; i >= 0; i--) {
            const candidate = billingPlans.plans.find((plan) => plan.type === PLAN_SORT_ORDER[i])
            if (candidate) return candidate
        }
        return undefined
    }, [currentPlan, billingPlans])

    const canDowngrade = Boolean(canChangePlanInApp && downgradeTargetPlan)

    // Open the Plans tab on the segment that holds the suggested lower plan, highlighting it — the
    // user picks and confirms the downgrade there rather than committing to a specific plan here.
    const goToDowngradePlans = () => setOptions({ activeTab: 'plans', highlightedPlan: downgradeTargetPlan?.type, plansSource: 'billing_settings' })

    const reactivateSubscriptionAsync = billingUser?.reactivateSubscriptionAsync
    const [isReactivateLoading, setIsReactivateLoading] = useState(false)

    const handleReactivate = async () => {
        if (
            !reactivateSubscriptionAsync ||
            !subscription?.subscriptionType ||
            !subscription?.subscriptionBillingInterval
        )
            return

        setIsReactivateLoading(true)
        try {
            await reactivateSubscriptionAsync({
                organizationId,
                subscriptionType: subscription.subscriptionType,
                interval: subscription.subscriptionBillingInterval,
            })
            await new Promise((resolve) => {
                setTimeout(() => {
                    invalidateBillingCache?.()
                    resolve(true)
                }, 5000)
            })
        } finally {
            setIsReactivateLoading(false)
        }
    }


    const openManagePortal = async () => {
        if (!canChangePlanInApp) {
            if (manageBillingUrl) {
                window.open(manageBillingUrl, '_blank', 'noopener,noreferrer')
            }
            return
        }

        setIsPortalLoading(true)
        try {
            const { url: stripeUrl } = await createPortalSessionAsync({ organizationId })
            if (stripeUrl) {
                window.open(stripeUrl, '_blank', 'noopener,noreferrer')
            }
        } finally {
            setIsPortalLoading(false)
        }
    }

    const handleCancelConfirm = async (cancellationReason: string) => {
        if (cancellationTargets.length === 0) return

        setIsCancelDialogOpen(false)
        setIsCancelLoading(true)

        try {
            for (const cancellationTarget of cancellationTargets) {
                await cancelSubscriptionAsync({
                    organizationId,
                    cancellationReason,
                    subscriptionUuid: cancellationTarget.subscriptionUuid,
                })
            }

            await new Promise((resolve) => {
                setTimeout(() => {
                    invalidateBillingCache?.()
                    resolve(true)
                }, 5000)
            })
        } finally {
            setIsCancelLoading(false)
        }
    }

    return (
        <div className="flex flex-col gap-6">
            {shopifyReconnectRequired && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-700 shrink-0" />
                    <div>
                        <p className="font-medium text-amber-900">Reconnect Shopify required</p>
                        <p className="text-sm text-amber-800">
                            Shopify access was revoked for this workspace.{' '}
                            <button
                                className="underline underline-offset-2 font-medium text-amber-900 hover:text-amber-950"
                                onClick={() =>
                                    window.open(
                                        shopifyReconnectUrl ?? '/shopify/install',
                                        '_blank',
                                        'noopener,noreferrer'
                                    )
                                }
                            >
                                Reconnect Shopify
                            </button>{' '}
                            to restore billing sync and Shopify-managed plan controls.
                        </p>
                    </div>
                </div>
            )}

            {cancellationTargets.length > 0 && (
                <BillingCancelDialog
                    isOpen={isCancelDialogOpen}
                    onClose={() => setIsCancelDialogOpen(false)}
                    onConfirm={handleCancelConfirm}
                    isLoading={isCancelLoading}
                    {...cancelDialogProps}
                />
            )}

            {/* Current Plan Section */}
            <div className="flex flex-col gap-4">
                <h3 className="text-[16px] font-semibold text-[#171717]">Current Plan</h3>

                <div className="bg-[#FAFAFA] rounded-xl p-4 border border-[#E5E7EB]">
                    {currentPlan ? (
                        <div className="flex flex-col gap-3">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-white border border-[#E5E7EB] flex items-center justify-center">
                                        <Image
                                            src={PLAN_APP_ICON_SRC[currentPlan.type] ?? PLAN_APP_ICON_SRC.CUSTOM}
                                            alt=""
                                            width={32}
                                            height={32}
                                            className="size-8 rounded-[10px]"
                                            aria-hidden
                                        />
                                    </div>
                                    <div>
                                        <div className="text-[15px] font-semibold text-[#171717]">
                                            {currentPlan.name} Plan
                                        </div>
                                        <div className="text-[13px] text-[#6B7280]">
                                            {subscription?.subscriptionBillingInterval === 'MONTHLY'
                                                ? 'Billed monthly'
                                                : 'Billed yearly'}
                                        </div>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-[18px] font-semibold text-[#171717]">
                                        {currentPlan.monthlyVersion?.discount ? (
                                            <>
                                                <span className="text-[14px] font-medium text-[#9CA3AF] line-through mr-1">
                                                    ${((currentPlan.monthlyVersion.price ?? 0) / 100).toLocaleString()}
                                                </span>
                                                $
                                                {(
                                                    currentPlan.monthlyVersion.discount.actualPrice / 100
                                                ).toLocaleString()}
                                            </>
                                        ) : (
                                            <>${((currentPlan.monthlyVersion?.price ?? 0) / 100).toLocaleString()}</>
                                        )}
                                        <span className="text-[13px] font-normal text-[#6B7280]">/mo</span>
                                    </div>
                                    {currentPlan.monthlyVersion?.discount && currentPlan.monthlyVersion.price && (
                                        <div className="mt-0.5">
                                            <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
                                                You save $
                                                {(
                                                    (currentPlan.monthlyVersion.price -
                                                        currentPlan.monthlyVersion.discount.actualPrice) /
                                                    100
                                                ).toLocaleString()}
                                                {currentPlan.monthlyVersion.discount.duration === 'once'
                                                    ? ' on your next month'
                                                    : '/mo'}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {subscription?.subscriptionEndsAt && subscription?.subscriptionCanceledAt && (
                                <div
                                    className={`text-[13px] px-3 py-2 rounded-lg ${
                                        pendingDowngradePlanName
                                            ? 'text-[#6B7280] bg-[#F3F4F6]'
                                            : 'text-[#DC2626] bg-[#FEF2F2]'
                                    }`}
                                >
                                    {pendingDowngradePlanName
                                        ? `Your plan changes to ${pendingDowngradePlanName} on `
                                        : 'Your subscription will end on '}
                                    {parseApiDate(subscription.subscriptionEndsAt)?.toFormat('DDD', {
                                        locale: 'en-US',
                                    })}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="text-[14px] text-[#6B7280]">You don't have an active subscription.</div>
                    )}
                </div>
            </div>

            {/* Manage Subscription Section */}
            {showPortalSection && (
                <SettingsSection
                    title={
                        !canChangePlanInApp
                            ? `Manage Subscription in ${externalBillingDashboardLabel}`
                            : showInvoiceHistoryOnly
                              ? 'View Invoices'
                              : 'Manage Payment Methods and Invoices'
                    }
                    description={
                        !canChangePlanInApp
                            ? `Change plan, view receipts, or cancel from ${externalBillingDashboardLabel}.`
                            : showInvoiceHistoryOnly
                              ? 'View and download past invoices.'
                              : 'Update payment methods, view billing history, and download invoices.'
                    }
                    cta={
                        <Button
                            variant="secondary"
                            onClick={openManagePortal}
                            disabled={
                                !organizationId ||
                                isPortalLoading ||
                                isCancelLoading ||
                                Boolean(!canChangePlanInApp && !manageBillingUrl)
                            }
                        >
                            {isPortalLoading
                                ? 'Opening...'
                                : !canChangePlanInApp
                                  ? `Open ${externalBillingDashboardLabel}`
                                  : showInvoiceHistoryOnly
                                    ? 'View Invoices'
                                    : 'Manage'}
                        </Button>
                    }
                />
            )}

            {canCancelInApp && (
            <SettingsSection
                title={
                    canReactivateSubscription
                        ? 'Reactivate Subscription'
                        : canDowngrade
                          ? isInTrial
                              ? 'Downgrade Trial'
                              : 'Downgrade Plan'
                          : isInTrial
                            ? 'Cancel Trial'
                            : 'Cancel Subscription'
                }
                description={
                    canReactivateSubscription
                        ? 'Your subscription has been cancelled. You can reactivate it before the billing period ends.'
                        : canDowngrade
                          ? 'Spend less by switching to a smaller plan — you keep all your work. You can still cancel if you prefer.'
                          : isInTrial
                            ? 'Cancel your trial by clicking cancel, a detailed reason helps us to improve.'
                            : 'Cancel your subscription by clicking cancel, a detailed reason helps us to improve.'
                }
                cta={
                    canReactivateSubscription ? (
                        <Button
                            variant="secondary"
                            onClick={handleReactivate}
                            disabled={!organizationId || isReactivateLoading}
                        >
                            {isReactivateLoading ? 'Reactivating...' : 'Reactivate'}
                        </Button>
                    ) : canDowngrade ? (
                        <div className="flex flex-col items-end gap-2">
                            <Button
                                variant="ds2-primary"
                                size="ds2"
                                onClick={goToDowngradePlans}
                                disabled={!organizationId || isPortalLoading || isCancelLoading}
                            >
                                {isInTrial ? 'Downgrade trial' : 'Downgrade plan'}
                            </Button>
                            <button
                                type="button"
                                onClick={() => setIsCancelDialogOpen(true)}
                                disabled={
                                    !organizationId || isPortalLoading || isCancelLoading || !canCancelSubscription
                                }
                                className="text-[13px] text-[#9CA3AF] underline underline-offset-2 transition-colors hover:text-[#6B7280] disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {isInTrial ? 'Cancel trial' : 'Cancel subscription'}
                            </button>
                        </div>
                    ) : (
                        <Button
                            variant="secondary"
                            onClick={() => setIsCancelDialogOpen(true)}
                            disabled={
                                !organizationId || isPortalLoading || isCancelLoading || !canCancelSubscription
                            }
                        >
                            Cancel
                        </Button>
                    )
                }
            />
            )}
        </div>
    )
}
